import { isRoleModuleEnabled } from './roleModuleAccess.js';

export const isExternalNetworkEnabled = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  const features = user.company_features || user.company_can_access || {};
  return user.network_mode === 'EXTERNAL' && Boolean(features.agent_network);
};

/** Shipment list All / Company / Network tabs — only for EXTERNAL companies with agent network. */
export const showShipmentNetworkScopeTabs = (user) => {
  if (!user) return false;
  const features = user.company_features || user.company_can_access || {};
  return user.network_mode === 'EXTERNAL' && Boolean(features.agent_network);
};

export const hasAttendanceAccess = (user) => {
  if (!user || user.role === 'Driver') return false;
  if (!isRoleModuleEnabled(user, 'attendance')) return false;
  const companyAccess = user.company_features || user.company_can_access || {};
  const isSystemAdmin = user.role === 'System Admin';
  return (isSystemAdmin || Boolean(companyAccess.attendance)) && user.network_mode !== 'EXTERNAL';
};

const attendanceMethodEnabled = (user, methodKey) => {
  if (!hasAttendanceAccess(user)) return false;
  const features = user.company_features || user.company_can_access || {};
  if (Object.prototype.hasOwnProperty.call(features, methodKey)) {
    return Boolean(features[methodKey]);
  }
  return true;
};

/** Header clock-in icon — company Attendance → Manual. */
export const canManualAttendance = (user) => attendanceMethodEnabled(user, 'attendance_manual');

/** Scan QR modal Attendance tab — company Attendance → QR. */
export const canScanQrAttendance = (user) => attendanceMethodEnabled(user, 'attendance_qr');

/** INTERNAL — Paid Amount Settlement (remit Paid collections). Not limited to cargo. */
export const hasPaidAmountSettlementAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  return true;
};

/** INTERNAL + can_access.commission — ML agent commission (not EXTERNAL XCOM). Cargo optional. */
export const hasMlAgentCommissionAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const features = user.company_features || user.company_can_access || {};
  if (!features.commission) return false;
  const role = user.role_name || user.role;
  if (role === 'Contract Party Admin') return true;
  return ['System Admin', 'Admin', 'Accountant', 'Owner'].includes(role);
};

/** Company can_access.shipment — drives shipment menus and dashboard fetches. */
export const hasShipmentFeatureAccess = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.shipment);
};

/** INTERNAL companies with commission enabled — dashboard cards/charts (includes Agent / Hub Admin). */
export const hasInternalCommissionDashboardAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.commission);
};

/** Loading, unloading, and commission stay on the cashbook until Book & Delivery Comm. */
export const usesLabourKeepCommissionRemit = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.commission);
};

export const hasOnContractAccess = (user) => {
  if (!user) return false;
  const features = user.company_features || user.company_can_access || {};
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  if (user.network_mode === 'EXTERNAL') return Boolean(features.external_on_contract);
  return Boolean(features.on_contract);
};

export const hasOnContractTruckAccess = (user) => {
  if (!user) return false;
  const features = user.company_features || user.company_can_access || {};
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  return Boolean(features.on_contract_truck);
};

export const isContractPartyAdmin = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  return role === 'Contract Party Admin' && user.network_mode !== 'EXTERNAL';
};

export const isHubAdmin = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  return role === 'Hub Admin';
};

export const hasContractPartyAdminAccess = isContractPartyAdmin;

export const hasExternalCommissionAccess = (user) => {
  if (!user) return false;
  if (user.network_mode !== 'EXTERNAL') return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.external_commission) || Boolean(features.commission);
};

/** Agent My Partnerships tab — controlled by company can_access.my_partnerships. */
export const hasMyPartnershipsAccess = (user) => {
  if (!user) return false;
  const role = user.role_name || user.role;
  if (role === 'System Admin') return true;
  if (role !== 'Agent') return false;
  const features = user.company_features || user.company_can_access || {};
  return Boolean(features.my_partnerships);
};

/** Shipment crossing — internal company + company setting enabled. */
export const hasShipmentCrossingAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  if (user.is_crossing_enabled === false) return false;
  if (!isRoleModuleEnabled(user, 'crossing')) return false;
  const role = user.role_name || user.role;
  return ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(role);
};

/** Internal Agent flagged as pickup man (same role, simplified booking). */
export const isPickupMan = (user) =>
  Boolean(user?.is_pickup_man) &&
  (user.role === 'Agent' || user.role_name === 'Agent') &&
  user.network_mode !== 'EXTERNAL';

export const hasInternalHubDispatchControls = (user) =>
  Boolean(user) && user.network_mode !== 'EXTERNAL';

export const isCompanyAdminRole = (user) => {
  if (!user) return false;
  const role = user.role || user.role_name;
  return role === 'Admin' || role === 'System Admin';
};

/** Create Dispatch form and QR Create — hub flag allow_dispatch (Scan QR dispatch flow is separate). */
export const hasLocationHubDispatchAccess = (user) => {
  if (!user) return false;
  if (isCompanyAdminRole(user)) return true;
  if (!user.location_hub_id) return false;
  return user.location_hub_allow_dispatch === true;
};

/** Hub booking menus — false when assigned hub has allow_booking disabled.
 *  Pickup man chooses FROM on the simplified form, so their assigned hub does not block booking.
 */
export const hasLocationHubBookingAccess = (user) => {
  if (!user) return false;
  if (['System Admin', 'Admin', 'Hub Admin'].includes(user.role)) return true;
  if (isPickupMan(user)) return true;
  if (!user.location_hub_id) return true;
  return user.location_hub_allow_booking !== false;
};

const _hubDispatchTypes = (user) => {
  const types = user?.location_hub_dispatch_types;
  if (Array.isArray(types)) return types.map((t) => String(t).toUpperCase());
  if (types) return String(types).split(',').map((t) => t.trim().toUpperCase()).filter(Boolean);
  return [];
};

/** ITCL hub Agent / Hub Admin — can create ITCL (OGCL transfer) requests. */
export const hasOgclTransferCreateAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  const role = user.role || user.role_name;
  if (!['Agent', 'Hub Admin'].includes(role)) return false;
  if (user.can_create_ogcl_transfer === true) return true;
  if (user.can_create_ogcl_transfer === false) return false;
  return _hubDispatchTypes(user).includes('ITCL');
};

/** Destination OGCL hub Agent / Hub Admin — incoming ITCL requests after approval. */
export const hasOgclIncomingAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  const role = user.role || user.role_name;
  if (!['Agent', 'Hub Admin'].includes(role)) return false;
  return _hubDispatchTypes(user).includes('OGCL');
};

/**
 * Hub Admin at an OGCL destination (and OGCL-only agents) dispatch approved
 * incoming ITCL shipments on an ML sheet — they do not create another ITCL request.
 */
export const shouldDispatchIncomingOgclOnly = (user) => {
  if (!hasOgclIncomingAccess(user)) return false;
  const role = user.role || user.role_name;
  if (role === 'Hub Admin') return true;
  return !hasOgclTransferCreateAccess(user);
};

/** ITCL create is for ITCL-hub Agents only. Hub Admin reviews/unloads, never creates. */
export const canShowOgclTransferCreate = (user) => {
  if (!hasOgclTransferCreateAccess(user)) return false;
  const role = user.role || user.role_name;
  if (role === 'Hub Admin') return false;
  return true;
};

/**
 * First tab on ITCL Requests. Incoming-only users land on Unload,
 * but they must still be able to switch to Approved / Pending / Rejected.
 */
export const defaultItclRequestTab = (user) => {
  if (hasOgclIncomingAccess(user) && !canShowOgclTransferCreate(user)) return 'unload';
  return 'pending';
};

/** Keep the tab the user selected. Map the removed To Dispatch tab to Unload. */
export const resolveItclRequestTab = (user, selectedTab) => {
  if (selectedTab && selectedTab !== 'incoming') return selectedTab;
  return defaultItclRequestTab(user);
};

/** Sidebar / list:
 * - ITCL hub Agent/Hub Admin (create + own requests)
 * - Destination OGCL Agent (incoming approved)
 * - Hub Admin / Admin / System Admin (approve)
 * Hidden for other roles.
 */
export const hasOgclTransferAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  if (hasOgclTransferCreateAccess(user)) return true;
  if (hasOgclIncomingAccess(user)) return true;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Hub Admin'].includes(role);
};

/** ITCL Requests sidebar/list — create users, dest OGCL unload users, company admins. */
export const hasOgclTransferRequestMenuAccess = (user) => {
  if (!hasInternalHubDispatchControls(user)) return false;
  if (hasOgclTransferCreateAccess(user)) return true;
  if (hasOgclIncomingAccess(user)) return true;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Hub Admin'].includes(role);
};

/** Auto ML Creation on Add Shipment — internal company + company setting enabled. */
export const hasAutoMlAccess = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  if (user.is_auto_ml_enabled === false) return false;
  return true;
};

/** New ML sheet via Add Dispatch form — admins, or hub Agent/Hub Admin when allow_dispatch is on. */
export const canCreateMlSheet = (user) => {
  if (!user) return false;
  if (isPickupMan(user)) return false;
  if (isCompanyAdminRole(user)) return true;
  const role = user.role || user.role_name;
  if (['Agent', 'Hub Admin'].includes(role)) {
    return hasLocationHubDispatchAccess(user);
  }
  return false;
};

/** QR Create (new sheet from photo) — same allow_dispatch gate as Create Dispatch form. */
export const canCreateMlSheetByQr = (user) => canCreateMlSheet(user);

/** Add booked shipments onto an existing ML sheet (list row action). */
export const canAddMlSheetShipments = (user) => {
  if (!user) return false;
  const role = user.role || user.role_name;
  return ['System Admin', 'Admin', 'Agent', 'Hub Admin'].includes(role);
};

/**
 * Origin/dispatch hubs often turn off Allow Delivery but still dispatch via Scan QR.
 * Receive/delivery stays enforced on the backend by destination hub.
 */
export const agentHubAllowsShipmentStatusUpdates = (user) => {
  if (!user) return false;
  const role = user.role || user.role_name;
  if (!['Agent', 'Hub Admin'].includes(role)) return true;
  return (
    user.location_hub_allow_delivery !== false
    || user.location_hub_allow_booking !== false
    || user.location_hub_allow_dispatch === true
  );
};

/** Scan QR — Update Status tab (shipment QR, not hub attendance QR). */
export const canScanQrUpdateStatus = (user) => {
  if (!user) return false;
  if (user.network_mode === 'EXTERNAL') return false;
  const role = user.role || user.role_name;
  if (!['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'].includes(role)) return false;
  const companyAccess = user.company_features || user.company_can_access || {};
  const hasShipment = role === 'System Admin' || Boolean(companyAccess.shipment);
  if (!hasShipment) return false;
  if (!isRoleModuleEnabled(user, 'update_status')) return false;
  return agentHubAllowsShipmentStatusUpdates(user);
};

export const canOpenScanQrModal = (user) =>
  canScanQrAttendance(user) || canScanQrUpdateStatus(user) || hasShipmentCrossingAccess(user);

/** ML create/edit: internal companies require at least one access agent. */
export const isMlAccessUsersRequired = (user) =>
  Boolean(user) && user.network_mode !== 'EXTERNAL';

/** Shipment receipt layouts: off | large | short | legal | laser3 | thermal */
export const RECEIPT_SIZE_VALUES = ['off', 'large', 'short', 'legal', 'laser3', 'thermal'];

export const RECEIPT_LAYOUT_LABELS = {
  off: 'No print',
  large: 'Large',
  short: 'Short',
  legal: 'Legal page',
  laser3: 'Laser (3 inch)',
  thermal: '3 inch thermal',
};

export const normalizeReceiptSize = (value, fallback = 'large') => {
  const text = String(value || '').trim().toLowerCase();
  return RECEIPT_SIZE_VALUES.includes(text) ? text : fallback;
};

export const getShipmentReceiptSize = (user) =>
  normalizeReceiptSize(user?.shipment_receipt_size, 'large');

export const getBookingReceiptSize = (user) =>
  normalizeReceiptSize(user?.booking_receipt_size, getShipmentReceiptSize(user));

export const getDeliveryReceiptSize = (user) =>
  normalizeReceiptSize(user?.delivery_receipt_size, getShipmentReceiptSize(user));

export const getReceiptSizeForEvent = (user, event) => {
  if (String(event || '').toLowerCase() === 'delivery') {
    return getDeliveryReceiptSize(user);
  }
  if (String(event || '').toLowerCase() === 'booking') {
    return getBookingReceiptSize(user);
  }
  return getShipmentReceiptSize(user);
};

export const shouldAutoPrintReceipt = (user, event) =>
  getReceiptSizeForEvent(user, event) !== 'off';

export const hubAllowedPaymentModes = (user) => {
  const modes = [];
  if (user?.location_hub_allow_to_pay !== false) modes.push('to_pay');
  if (user?.location_hub_allow_paid !== false) modes.push('paid');
  if (user?.location_hub_allow_on_account !== false) modes.push('credit');
  return modes.length ? modes : ['to_pay', 'paid', 'credit'];
};

export default isExternalNetworkEnabled;
